import {
  Box3,
  Color,
  Vector3,
  type ProjectionType,
  type ViewPreset,
  type Web3DViewer,
} from '@technology/web3d';
import {
  MarkupPlugin,
  PolygonMeasurementTool,
  type PolygonMeasurement,
} from '@technology/web3d-plugin-markup';
import { getLogger } from '@trimble/logging';
import { batch, createSignal, type Accessor } from 'solid-js';
import { SingleCapabilityProviderBase } from '../base/single-provider-base';
import { registerExternalMeasurementEventOrigin } from '../measurements/measurements-provider';
import { SnapType } from '../snapping/snap-type';
import {
  closeRing,
  emphasizeStyle,
  extractExteriorRings,
  resolveGeofencePolygonStyle,
} from './geofence-polygon-utils';
import {
  GEOFENCE_POLYGON_ORIGIN,
  type GeofenceCoordinateSpace,
  type GeofencePolygonEmphasis,
  type GeofencePolygonInput,
  type GeofencePolygonSkipped,
  type GeofencePolygonStyle,
  type GeofencePolygonsOptions,
  type GeofencePositionResolver,
} from './geofence-polygon.types';

registerExternalMeasurementEventOrigin(GEOFENCE_POLYGON_ORIGIN);

const DEFAULT_ELEVATION_OFFSET = 0.1;
const SELECTED_RENDER_ORDER = 1;
const DEFAULT_RENDER_ORDER = 0;
const LAYER_RENDER_ORDER_STEP = 10;
const POLYGON_ZOOM_TIGHTNESS = 1.2;
const POLYGON_ZOOM_MARGIN_RATIO = 0.1;

export class GeofencePolygonsCapabilityProvider extends SingleCapabilityProviderBase<GeofencePolygonInput> {
  public idKey = 'id' as const;

  public readonly visibleIds: Accessor<ReadonlySet<string>>;
  public readonly selectedIds: Accessor<ReadonlySet<string>>;
  public readonly highlightedIds: Accessor<ReadonlySet<string>>;
  public readonly renderedPolygonIds: Accessor<ReadonlySet<string>>;
  public readonly skippedPolygonIds: Accessor<ReadonlySet<string>>;

  private readonly setVisibleIds: (value: ReadonlySet<string>) => void;
  private readonly setSelectedIds: (value: ReadonlySet<string>) => void;
  private readonly setHighlightedIds: (value: ReadonlySet<string>) => void;
  private readonly setRenderedPolygonIds: (value: ReadonlySet<string>) => void;
  private readonly setSkippedPolygonIds: (value: ReadonlySet<string>) => void;
  private readonly rendered = new Map<string, PolygonMeasurement[]>();
  private readonly appliedStyles = new Map<string, GeofencePolygonStyle>();
  private readonly skipped = new Map<string, GeofencePolygonSkipped>();
  private readonly skippedListeners = new Set<
    (skipped: GeofencePolygonSkipped) => void
  >();
  private readonly options: GeofencePolygonsOptions;
  private positionResolver?: GeofencePositionResolver;
  private lonLatPositionResolver?: GeofencePositionResolver;
  private elevation?: number;
  private coordinateSpace: GeofenceCoordinateSpace = 'lonlat';

  constructor(viewer: Web3DViewer, options: GeofencePolygonsOptions = {}) {
    super(viewer);
    this.options = options;
    const [visibleIds, setVisibleIds] = createSignal<ReadonlySet<string>>(
      new Set()
    );
    const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<string>>(
      new Set()
    );
    const [highlightedIds, setHighlightedIds] = createSignal<
      ReadonlySet<string>
    >(new Set());
    const [renderedPolygonIds, setRenderedPolygonIds] = createSignal<
      ReadonlySet<string>
    >(new Set());
    const [skippedPolygonIds, setSkippedPolygonIds] = createSignal<
      ReadonlySet<string>
    >(new Set());
    this.visibleIds = visibleIds;
    this.setVisibleIds = setVisibleIds;
    this.selectedIds = selectedIds;
    this.setSelectedIds = setSelectedIds;
    this.highlightedIds = highlightedIds;
    this.setHighlightedIds = setHighlightedIds;
    this.renderedPolygonIds = renderedPolygonIds;
    this.setRenderedPolygonIds = setRenderedPolygonIds;
    this.skippedPolygonIds = skippedPolygonIds;
    this.setSkippedPolygonIds = setSkippedPolygonIds;
  }

  public setPositionResolver(
    resolver: GeofencePositionResolver | undefined
  ): void {
    this.positionResolver = resolver;
    this.rebuild();
  }

  public setLonLatPositionResolver(
    resolver: GeofencePositionResolver | undefined
  ): void {
    this.lonLatPositionResolver = resolver;
    this.rebuild();
  }

  public setCoordinateSpace(space: GeofenceCoordinateSpace): void {
    this.coordinateSpace = space;
    this.rebuild();
  }

  public setElevation(elevation: number | undefined): void {
    this.elevation = elevation;
    this.rebuild();
  }

  public setPolygons(polygons: readonly GeofencePolygonInput[]): void {
    batch(() => {
      const byId = new Map<string, GeofencePolygonInput>();
      for (const polygon of polygons) {
        const id = polygon.id.trim();
        if (!id) continue;
        if (byId.has(id)) {
          getLogger().warn(
            `Duplicate geofence polygon id "${id}"; using the last value`
          );
        }
        byId.set(id, { ...polygon, id });
      }
      this.removeRendered();
      this.setItems([...byId.values()]);
      this.resetSkipped();
      this.setVisibleIds(this.keepKnown(this.visibleIds(), byId));
      this.setSelectedIds(this.keepKnown(this.selectedIds(), byId));
      this.setHighlightedIds(this.keepKnown(this.highlightedIds(), byId));
      this.reconcile();
    });
  }

  public clear(): void {
    batch(() => {
      this.removeRendered();
      this.setItems([]);
      this.resetSkipped();
      this.setVisibleIds(new Set());
      this.setSelectedIds(new Set());
      this.setHighlightedIds(new Set());
    });
  }

  public setVisible(
    ids: readonly string[] | undefined,
    visible: boolean
  ): void {
    batch(() => {
      const next = new Set(this.visibleIds());
      const targetIds = ids ?? this.items().map((polygon) => polygon.id);
      for (const id of targetIds) {
        if (visible) next.add(id);
        else next.delete(id);
      }
      this.setVisibleIds(next);
      this.reconcile();
    });
  }

  public setSelected(ids: readonly string[]): void {
    batch(() => {
      this.setSelectedIds(new Set(ids));
      this.reconcile();
    });
  }

  public setHighlighted(ids: readonly string[]): void {
    batch(() => {
      this.setHighlightedIds(new Set(ids));
      this.reconcile();
    });
  }

  public onSkipped(
    listener: (skipped: GeofencePolygonSkipped) => void
  ): () => void {
    this.skippedListeners.add(listener);
    return () => this.skippedListeners.delete(listener);
  }

  public async zoomToPolygons(
    ids: readonly string[],
    viewPreset: ViewPreset = 'top' as ViewPreset
  ): Promise<void> {
    const box = new Box3();
    let hasPoints = false;
    for (const id of ids) {
      const markups = this.rendered.get(id);
      if (!markups) {
        continue;
      }
      for (const markup of markups) {
        const picks = markup.picks;
        for (const pick of picks) {
          const point = pick.point;
          if (!point) {
            continue;
          }
          box.expandByPoint(point);
          hasPoints = true;
        }
      }
    }
    if (!hasPoints) {
      return;
    }
    if (ids.length > 1) {
      const size = new Vector3();
      box.getSize(size);
      const margin = Math.max(size.x, size.y) * POLYGON_ZOOM_MARGIN_RATIO;
      if (margin > 0) {
        box.expandByScalar(margin);
      }
    }
    if (viewPreset === ('top' as ViewPreset)) {
      await this.viewer.camera.setProjectionType('ortho' as ProjectionType);
    }
    await this.viewer.camera.fitToView({
      box,
      tightness: POLYGON_ZOOM_TIGHTNESS,
      viewPreset,
    });
  }

  public override async cleanup(): Promise<void> {
    this.clear();
    this.skippedListeners.clear();
  }

  private rebuild(): void {
    this.removeRendered();
    this.resetSkipped();
    this.reconcile();
  }

  private resetSkipped(): void {
    this.skipped.clear();
    this.setSkippedPolygonIds(new Set());
  }

  private keepKnown(
    ids: ReadonlySet<string>,
    polygons: ReadonlyMap<string, GeofencePolygonInput>
  ): ReadonlySet<string> {
    return new Set([...ids].filter((id) => polygons.has(id)));
  }

  private get markupPlugin(): MarkupPlugin {
    const plugin = this.viewer.plugins.get(MarkupPlugin);
    if (!plugin) throw new Error('MarkupPlugin is not available on the viewer');
    return plugin;
  }

  private removeRendered(): void {
    const plugin = this.markupPlugin;
    const currentIds = new Set(plugin.getMarkups().map((markup) => markup.id));
    const ids = [...this.rendered.values()]
      .flat()
      .map((markup) => markup.id)
      .filter((id) => currentIds.has(id));
    if (ids.length > 0) plugin.removeById(ids);
    this.rendered.clear();
    this.appliedStyles.clear();
    this.setRenderedPolygonIds(new Set());
  }

  private reconcile(): void {
    const ready =
      this.positionResolver !== undefined && this.elevation !== undefined;
    for (const polygon of this.items()) {
      const shouldShow = ready && this.visibleIds().has(polygon.id);
      const existing = this.rendered.get(polygon.id);
      if (!shouldShow) {
        if (existing) {
          this.removeRenderedPolygon(polygon.id);
        }
        continue;
      }
      if (!existing && !this.skipped.has(polygon.id)) {
        this.createPolygon(polygon);
      }
      this.applyStyle(polygon.id);
    }
    this.viewer.waitForRender();
  }

  private removeRenderedPolygon(id: string): void {
    const markups = this.rendered.get(id);
    if (!markups) return;
    const currentIds = new Set(
      this.markupPlugin.getMarkups().map((markup) => markup.id)
    );
    const ids = markups
      .map((markup) => markup.id)
      .filter((markupId) => currentIds.has(markupId));
    if (ids.length > 0) this.markupPlugin.removeById(ids);
    this.rendered.delete(id);
    this.appliedStyles.delete(id);
    this.syncRenderedPolygonIds();
  }

  private createPolygon(polygon: GeofencePolygonInput): void {
    const coordinateSpace = polygon.coordinateSpace ?? this.coordinateSpace;
    const resolver =
      coordinateSpace === 'lonlat'
        ? (this.lonLatPositionResolver ?? this.positionResolver)
        : this.positionResolver;
    const elevation = this.elevation;
    if (!resolver || elevation === undefined) return;
    const extracted = extractExteriorRings(polygon.geometry, coordinateSpace);
    if ('skipped' in extracted) {
      if (
        extracted.skipped === 'invalid-coordinates' &&
        coordinateSpace === 'lonlat'
      ) {
        getLogger().warn(
          'Geofence coordinates are outside lon/lat range; project grid coordinates need a calibrated project (CSIB) to be placed on the map',
          { id: polygon.id }
        );
      }
      this.reportSkipped(polygon.id, extracted.skipped);
      return;
    }
    const markups: PolygonMeasurement[] = [];
    for (const ring of extracted.rings) {
      try {
        const points = ring.map(([lon, lat]) => {
          const position = resolver(lon, lat);
          return new Vector3(
            position.x,
            position.y,
            elevation +
              (this.options.elevationOffset ?? DEFAULT_ELEVATION_OFFSET) *
                (1 + (polygon.layer ?? 0))
          );
        });
        const closed = closeRing(points);
        if (!closed) {
          this.reportSkipped(polygon.id, 'too-few-vertices');
          continue;
        }
        const style = resolveGeofencePolygonStyle(
          this.options.defaultStyle,
          polygon.style
        );
        const picks = closed.map((point) => ({
          snapType: Number(SnapType.POINT),
          point: point.clone(),
        }));
        const tool = this.viewer.tools.get(PolygonMeasurementTool);
        if (!tool) {
          throw new Error(
            'PolygonMeasurementTool is not available on the viewer'
          );
        }
        const markup = tool.add(
          picks,
          new Color(style.color),
          GEOFENCE_POLYGON_ORIGIN
        );
        if (markup.parent == null || !markup.isClosed) {
          const liveMarkupIds = new Set(
            this.markupPlugin.getMarkups().map((item) => item.id)
          );
          if (liveMarkupIds.has(markup.id)) {
            this.markupPlugin.removeById([markup.id]);
          }
          this.reportSkipped(polygon.id, 'self-intersecting');
          continue;
        }
        markup.editable = false;
        markup.userData['geofencePolygonId'] = polygon.id;
        markups.push(markup);
      } catch (error) {
        getLogger().warn('Failed to render geofence polygon', {
          id: polygon.id,
          error,
        });
        this.reportSkipped(polygon.id, 'render-failed');
      }
    }
    if (markups.length > 0) {
      this.rendered.set(polygon.id, markups);
      this.syncRenderedPolygonIds();
    }
  }

  private syncRenderedPolygonIds(): void {
    this.setRenderedPolygonIds(new Set(this.rendered.keys()));
  }

  private applyStyle(id: string): void {
    const polygon = this.items().find((item) => item.id === id);
    const markups = this.rendered.get(id);
    if (!polygon || !markups) return;
    const emphasis: GeofencePolygonEmphasis = this.selectedIds().has(id)
      ? 'selected'
      : this.highlightedIds().has(id)
        ? 'highlighted'
        : 'none';
    const style = emphasizeStyle(
      resolveGeofencePolygonStyle(this.options.defaultStyle, polygon.style),
      emphasis
    );
    const previousStyle = this.appliedStyles.get(id);
    if (
      previousStyle?.color === style.color &&
      previousStyle.fillOpacity === style.fillOpacity &&
      previousStyle.lineThickness === style.lineThickness &&
      previousStyle.lineStyle === style.lineStyle
    ) {
      return;
    }
    for (const markup of markups) {
      markup.color = new Color(style.color);
      markup.fillOpacity = style.fillOpacity;
      markup.lineThickness = style.lineThickness;
      markup.lineStyle = style.lineStyle;
      markup.traverse((child) => {
        child.renderOrder =
          (polygon.layer ?? 0) * LAYER_RENDER_ORDER_STEP +
          (emphasis === 'selected'
            ? SELECTED_RENDER_ORDER
            : DEFAULT_RENDER_ORDER);
      });
    }
    this.appliedStyles.set(id, style);
  }

  private reportSkipped(
    id: string,
    reason: GeofencePolygonSkipped['reason']
  ): void {
    const skipped = { id, reason };
    this.skipped.set(id, skipped);
    this.setSkippedPolygonIds(new Set(this.skipped.keys()));
    getLogger().warn('Geofence polygon skipped', skipped);
    for (const listener of this.skippedListeners) listener(skipped);
  }
}
